import {
    Box,
    Flex,
    Grid,
    GridItem,
    Text,
} from '@chakra-ui/react'
import React from 'react'
import { getShadeFromPercent } from '../../../../../utils/colors'
import { addCommasToNumber } from '../../../../../utils/helpers'


type Props = {
    locations: any
    data: any
}


export const BillingReportBody = ({ locations, data }: Props) => {
    const locationsWithStats = locations
        .map((loc) => {
            const percent = (loc.value / data.totalConsumption) * 100 || 0
            const cost = addCommasToNumber(loc.total_cost)
            const color = getShadeFromPercent('#157fee', percent)
            return { ...loc, percent, color, cost }
        })
        .sort((a, b) => b.percent - a.percent)

    const isGroupedByLocation = locations?.[0]?.location_id !== undefined

    return (
        <Flex direction="column" w="100%" px={10} mb={10} gap={8}>
            {/* Overview bar */}
            <Box
                rounded="xl"
                bg="white"
                px="20px"
                py="16px"
                className="consumption-by-location"
                boxShadow="sm"
                mt={5}
            >
                <Text fontWeight={700} fontSize="14px" mb={3}>Energy Consumption by {isGroupedByLocation ? "Location" : "Group"}</Text>
                {locationsWithStats.map((loc) => (
                    <Grid
                        key={loc?.location_id || loc?.group_id}
                        templateColumns="110px 1fr 80px"
                        alignItems="center"
                        gap={4}
                        mb={3}
                        px={3}
                    >
                        <Text
                            fontWeight={700}
                            fontSize="14px"
                            color="#707070"
                            overflowWrap="break-word"
                            wordBreak="break-word"
                            whiteSpace="normal"
                        >
                            {loc.title}
                        </Text>
                        <Box
                            position="relative"
                            height="56px"
                            width="100%"
                            bg="#f0f0f0"
                            borderRadius="md"
                            overflow="hidden"
                        >
                            {loc.percent > 0 && (
                                <Box
                                    bg={loc.color}
                                    height="100%"
                                    width={`${loc.percent}%`}
                                    transition="width 0.3s ease"
                                />
                            )}
                        </Box>
                        <Flex direction="column" alignItems="center" justifyContent="center">
                            <Text fontWeight="700" fontSize="14px">{Math.round(loc.percent)}%</Text>
                            <Text ml={2} color="gray.500" fontSize="12px" display="flex" gap={1}>
                                {loc.value.toLocaleString()}<span>kWh</span>
                            </Text>
                            <Text ml={2} color="gray.500" fontSize="12px" display="flex">{loc.cost} лв</Text>
                        </Flex>
                    </Grid>
                ))}
            </Box>
            <Flex direction="column">
                {/* Detailed breakdown per location */}
                {locations.map((location) => {
                    return (
                        <Box key={location.id} className='locations' borderRadius="30px" bg="white" px={6} py={6} boxShadow="xl">
                            <Text fontSize="16px" fontWeight="700" mb={4}>
                                {location.title}
                            </Text>

                            {/* Table Header */}
                            <Grid
                                templateColumns="1fr 1fr 1fr"
                                alignItems="center"
                                mb={2}
                                mt={2}
                                pr={2}
                                pl={0}
                            >
                                <Text fontSize="13px" fontWeight="700" color="#9B9B9B">
                                    Device
                                </Text>
                                <Text fontSize="13px" fontWeight="700" color="#9B9B9B" textAlign="right">
                                    Consumption
                                </Text>
                                <Text fontSize="13px" fontWeight="700" color="#9B9B9B" textAlign="right">
                                    Cost for consumption
                                </Text>
                            </Grid>

                            {/* Devices */}
                            {location?.children.map((device) =>
                                <Box key={device.id} mb={1}>
                                    <DeviceRow device={device} indent={0} />
                                    {device.children?.length > 0 &&
                                        renderDeviceChildrenRecursive(device.children, 1)}
                                </Box>
                            )}

                            {/* Total Row */}
                            <Grid
                                templateColumns="1fr 1fr 1fr"
                                alignItems="center"
                                py={2}
                                fontWeight="bold"
                                borderTop="1px solid #f1f1f1"
                            >
                                <Text fontSize="14px" color="gray.700"></Text>
                                <Text fontSize="14px" textAlign="right" color="gray.700">
                                    {addCommasToNumber(location.value)} <span style={{ fontWeight: 400, color: "#9B9B9B" }}>kWh</span>
                                </Text>
                                <Text fontSize="14px" textAlign="right" color="gray.700">
                                    {addCommasToNumber(location.total_cost)} <span style={{ fontWeight: 400, color: "#9B9B9B" }}>лв</span>
                                </Text>
                            </Grid>
                        </Box>
                    )
                })}
            </Flex>
        </Flex>
    )
}

const DeviceRow = ({ device, indent }) => {
    return (
        <Grid templateColumns="1fr 1fr 1fr" alignItems="center" py={1.5}>
            <GridItem>
                <Box display="flex" alignItems="center" position="relative">
                    {/* Tree connector */}
                    <Box
                        ml={`${indent * 20}px`}
                        display="flex"
                        alignItems="center"
                        position="relative"
                    >
                        {/* Device label */}
                        <Text fontSize="14px" color="#484848" fontWeight={indent === 0 ? 600 : 400}>
                            {device.title}
                        </Text>
                    </Box>
                </Box>
            </GridItem>

            <GridItem>
                <Text fontSize="14px" fontWeight="700" color="#707070" textAlign="right">
                    {addCommasToNumber(device.value)}{" "}
                    <span style={{ fontWeight: 400, color: "#9B9B9B" }}>kWh</span>
                </Text>
            </GridItem>

            <GridItem>
                <Text fontSize="14px" fontWeight="700" color="#707070" textAlign="right">
                    {addCommasToNumber(device.total_cost)}{" "}
                    <span style={{ fontWeight: 400, color: "#9B9B9B" }}>лв</span>
                </Text>
            </GridItem>
        </Grid>
    );
};


function renderDeviceChildrenRecursive(children, indent) {
    return (
        <>
            {children.map((child) => (
                <Box key={child.device_id}>
                    <DeviceRow device={child} indent={indent} />
                    {child.children?.length > 0 &&
                        renderDeviceChildrenRecursive(child.children, indent + 1)}
                </Box>
            ))}
        </>
    )
}
